import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Plus, Trash2, Users, Filter, Pencil, KeyRound, Copy, Mail, AlertTriangle, Search, UserCheck, X, Wand2, Eye, EyeOff, Upload, Download, FileSpreadsheet, ChevronLeft, ChevronRight } from 'lucide-react';

const ALL = '__all__';

const emptyForm = { username: '', password: '', full_name: '', department_id: '', project_id: '', job_title: '', email: '' };

export default function AdminPersonal() {
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filterDept, setFilterDept] = useState(ALL);
  const [filterProj, setFilterProj] = useState(ALL);

  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  // Autocomplete de empleados existentes (solo al crear nuevo personal)
  const [empQuery, setEmpQuery] = useState('');
  const [empOpen, setEmpOpen] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  // Modal de contraseña generada
  const [resetResult, setResetResult] = useState(null); // {username, fullName, newPassword, emailSent, email}
  // Confirmaciones (AlertDialog en lugar de window.confirm)
  const [resetConfirm, setResetConfirm] = useState(null); // user object o null
  const [deleteConfirm, setDeleteConfirm] = useState(null); // user object o null
  // Estado de SMTP (banner)
  const [emailStatus, setEmailStatus] = useState(null);
  // Paginación (20 por página, recientes primero)
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({ total: 0, pages: 1 });
  // Contraseñas visibles en tabla
  const [shownPwdIds, setShownPwdIds] = useState(new Set());
  // Mostrar contraseña en formulario
  const [showFormPwd, setShowFormPwd] = useState(false);
  // Alerta emergente de usuario duplicado
  const [dupAlert, setDupAlert] = useState(null);
  // Carga masiva
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);

  useEffect(() => {
    loadCats();
    api.getEmailStatus().then((r) => setEmailStatus(r.data)).catch(() => setEmailStatus(null));
  }, []);
  useEffect(() => { loadUsers(); /* eslint-disable-next-line */ }, [filterDept, filterProj, page]);

  // Detectar pre-rellenado desde la página Empleados ("Dar acceso a la Academia")
  useEffect(() => {
    const prefillEmp = location.state?.prefillEmployee;
    if (prefillEmp) {
      setForm({
        ...emptyForm,
        full_name: prefillEmp.name || '',
        email: prefillEmp.email || '',
        department_id: prefillEmp.department_id || '',
        username: prefillEmp.email && prefillEmp.email.includes('@')
          ? prefillEmp.email.split('@')[0].toLowerCase()
          : '',
      });
      setSelectedEmployeeId(prefillEmp.id);
      setEmpQuery(prefillEmp.name || '');
      setEditingId(null);
      setOpen(true);
      // Limpiar el state para que no se reabra al refrescar
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line
  }, [location.state]);

  const loadCats = async () => {
    try {
      const [d, p, e] = await Promise.all([api.getDepartments(), api.getProjects(), api.getEmployees()]);
      setDepartments(d.data);
      setProjects(p.data);
      setEmployees(e.data || []);
    } catch (e) { toast.error('Error al cargar catálogos'); }
  };

  const loadUsers = async () => {
    try {
      const params = { page, page_size: 20 };
      if (filterDept !== ALL) params.department_id = filterDept;
      if (filterProj !== ALL) params.project_id = filterProj;
      const res = await api.getPersonal(params);
      setUsers(res.data.items);
      setPageInfo({ total: res.data.total, pages: res.data.pages });
      if (res.data.items.length === 0 && page > 1) setPage(page - 1);
    } catch (e) { toast.error('Error al cargar personal'); }
  };

  const refreshToFirstPage = () => {
    if (page === 1) loadUsers(); else setPage(1);
  };

  const handleNewClick = () => {
    setForm(emptyForm);
    setEditingId(null);
    setEmpQuery('');
    setEmpOpen(false);
    setSelectedEmployeeId(null);
    setShowFormPwd(false);
    setOpen(true);
  };

  // Generar usuario con la regla: 1ª letra nombre + 1ª letra apellido paterno + apellido materno
  const handleGenerateUsername = async () => {
    if (!form.full_name.trim()) { toast.error('Escribe primero el nombre completo'); return; }
    try {
      const res = await api.generatePersonalCredentials(form.full_name);
      setForm((p) => ({ ...p, username: res.data.username }));
      if (res.data.exists) {
        setDupAlert(`El usuario "${res.data.username}" ya está dado de alta en el sistema. Revísalo antes de continuar o ajusta el usuario manualmente.`);
      } else {
        toast.success(`Usuario generado: ${res.data.username}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo generar el usuario');
    }
  };

  // Contraseña estilo sistema (ej. Woq81675)
  const handleGeneratePassword = () => {
    const r = (s) => s[Math.floor(Math.random() * s.length)];
    const c = 'bcdfghjkmnpqrstvwxz', v = 'aeiou', d = '0123456789';
    const pwd = r(c).toUpperCase() + r(v) + r(c) + Array.from({ length: 5 }, () => r(d)).join('');
    setForm((p) => ({ ...p, password: pwd }));
    setShowFormPwd(true);
  };

  const togglePwdVisibility = (id) => {
    setShownPwdIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.downloadPersonalTemplate();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'plantilla_personal.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Error al descargar la plantilla'); }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setBulkLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', bulkFile);
      const res = await api.bulkUploadPersonal(fd);
      setBulkResults(res.data);
      refreshToFirstPage();
      if (res.data.created > 0) toast.success(`${res.data.created} usuario(s) creado(s)`);
      if (res.data.duplicates > 0) toast.warning(`${res.data.duplicates} duplicado(s) — revísalos`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al procesar el archivo');
    } finally { setBulkLoading(false); }
  };

  const exportBulkResults = () => {
    if (!bulkResults) return;
    const rows = [['Fila', 'Nombre', 'Usuario', 'Contraseña', 'Estatus', 'Detalle']];
    bulkResults.results.forEach((r) => rows.push([r.row, r.full_name, r.username || '', r.password || '', r.status, r.error || '']));
    const csv = '\ufeff' + rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'credenciales_personal.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  // Sugerir un username basado en el correo o nombre del empleado seleccionado
  const suggestUsername = (employee) => {
    if (employee?.email && employee.email.includes('@')) {
      return employee.email.split('@')[0].toLowerCase();
    }
    if (employee?.name) {
      const parts = employee.name.trim().toLowerCase().split(/\s+/);
      if (parts.length >= 2) return `${parts[0]}.${parts[1]}`;
      return parts[0] || '';
    }
    return '';
  };

  const handleSelectEmployee = (employee) => {
    setSelectedEmployeeId(employee.id);
    setEmpQuery(employee.name);
    setEmpOpen(false);
    setForm((prev) => ({
      ...prev,
      full_name: employee.name || '',
      email: employee.email || '',
      department_id: employee.department_id || prev.department_id,
      // Solo sugerir username si está vacío para no sobreescribir lo que el admin ya escribió
      username: prev.username || suggestUsername(employee),
    }));
  };

  const handleClearSelectedEmployee = () => {
    setSelectedEmployeeId(null);
    setEmpQuery('');
  };

  // Empleados que aún no son personal (no tienen cuenta de usuario con ese email)
  const usedEmails = new Set(users.map((u) => (u.email || '').toLowerCase()).filter(Boolean));
  const filteredEmployees = (() => {
    const q = empQuery.trim().toLowerCase();
    if (!q) return employees.slice(0, 8);
    return employees
      .filter((emp) => {
        const inName = (emp.name || '').toLowerCase().includes(q);
        const inEmail = (emp.email || '').toLowerCase().includes(q);
        return inName || inEmail;
      })
      .slice(0, 8);
  })();

  const handleEditClick = (u) => {
    setForm({
      username: u.username || '',
      password: '',
      full_name: u.full_name || u.name || '',
      department_id: u.department_id || '',
      project_id: u.project_id || '',
      job_title: u.job_title || '',
      email: u.email || '',
    });
    setEditingId(u.id);
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingId) {
      // Editar: NO se actualizan username ni password aquí
      if (!form.full_name || !form.department_id || !form.project_id) {
        toast.error('Nombre, depto y proyecto son requeridos'); return;
      }
      setLoading(true);
      try {
        await api.updatePersonal(editingId, {
          full_name: form.full_name,
          department_id: form.department_id,
          project_id: form.project_id,
          job_title: form.job_title || null,
          email: form.email || null,
        });
        toast.success('Personal actualizado');
        setOpen(false);
        setEditingId(null);
        loadUsers();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Error al actualizar');
      } finally { setLoading(false); }
    } else {
      // Crear nuevo
      if (!form.username || !form.password || !form.full_name || !form.department_id || !form.project_id) {
        toast.error('Complete todos los campos requeridos'); return;
      }
      setLoading(true);
      try {
        await api.createPersonal(form);
        toast.success(form.email ? 'Personal creado · email enviado con credenciales' : 'Personal creado');
        setForm(emptyForm);
        setOpen(false);
        refreshToFirstPage();
      } catch (err) {
        const detail = err.response?.data?.detail || '';
        if (err.response?.status === 400 && detail.toLowerCase().includes('ya existe')) {
          setDupAlert(`El usuario "${form.username}" ya está dado de alta en el sistema. Revísalo antes de continuar o ajusta el usuario manualmente.`);
        } else {
          toast.error(detail || 'Error al crear personal');
        }
      } finally { setLoading(false); }
    }
  };

  const handleDelete = (u) => {
    setDeleteConfirm(u);
  };

  const confirmDelete = async () => {
    const u = deleteConfirm;
    setDeleteConfirm(null);
    if (!u) return;
    try {
      await api.deletePersonal(u.id);
      toast.success('Personal eliminado');
      loadUsers();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const handleResetPassword = (u) => {
    setResetConfirm(u);
  };

  const confirmResetPassword = async () => {
    const u = resetConfirm;
    setResetConfirm(null);
    if (!u) return;
    try {
      const r = await api.resetPersonalPassword(u.id);
      setResetResult({
        username: u.username,
        fullName: u.full_name,
        newPassword: r.data.new_password,
        emailSent: r.data.email_sent,
        emailConfigured: r.data.email_configured,
        userHasEmail: r.data.user_has_email,
        email: u.email,
      });
      if (r.data.email_sent) {
        toast.success('Contraseña enviada por correo');
      } else if (!r.data.email_configured) {
        toast.warning('SMTP no configurado · contraseña visible en pantalla');
      } else if (!r.data.user_has_email) {
        toast.warning('Usuario sin email · contraseña visible en pantalla');
      } else {
        toast.warning('No se pudo enviar el correo · contraseña visible en pantalla');
      }
      loadUsers();
    } catch (e) {
      toast.error('Error al restablecer contraseña');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copiado al portapapeles');
    } catch { toast.error('No se pudo copiar'); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl" data-testid="admin-personal-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-tight flex items-center gap-3">
              <Users className="h-7 w-7 text-[#DC143C]" />
              Personal
            </h1>
            <p className="text-gray-600 mt-1">Personal con acceso a la Academia</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setBulkOpen(true)} data-testid="bulk-upload-button" variant="outline" className="border-[#DC143C] text-[#DC143C] hover:bg-red-50 font-medium uppercase tracking-wide flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Carga Masiva
            </Button>
            <Button onClick={handleNewClick} data-testid="add-personal-button" className="bg-[#DC143C] hover:bg-[#B91C1C] text-white font-medium uppercase tracking-wide flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Agregar
            </Button>
          </div>
        </div>

        {/* Banner SMTP no configurado */}
        {emailStatus && !emailStatus.enabled && (
          <div className="mb-6 bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3" data-testid="smtp-warning-banner">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-medium text-amber-900">Envío de correos no configurado</p>
              <p className="text-amber-700 mt-1">
                No hay credenciales SMTP. Las contraseñas restablecidas se mostrarán en pantalla para que las compartas manualmente.
                Para enviar correos automáticos (bienvenida, reset, anuncios), configura SMTP_USER, SMTP_PASSWORD y SENDER_EMAIL en el backend (.env).
              </p>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 text-gray-600 text-sm font-medium uppercase tracking-wide">
            <Filter className="h-4 w-4" /> Filtros:
          </div>
          <div className="flex-1 min-w-[200px]">
            <Select value={filterDept} onValueChange={(v) => { setFilterDept(v); setPage(1); }}>
              <SelectTrigger data-testid="filter-dept-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos los departamentos</SelectItem>
                {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <Select value={filterProj} onValueChange={(v) => { setFilterProj(v); setPage(1); }}>
              <SelectTrigger data-testid="filter-project-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos los proyectos</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {users.length === 0 ? (
            <div className="p-12 text-center text-gray-500"><p>No hay personal registrado</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contraseña</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Departamento</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Proyecto</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Puesto</th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50 transition-colors" data-testid="personal-row">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">{u.username}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{u.full_name}</td>
                      <td className="px-6 py-4 text-sm">
                        {u.email ? (
                          <span className="inline-flex items-center gap-1 text-gray-700">
                            <Mail className="h-3 w-3" /> {u.email}
                          </span>
                        ) : (
                          <span className="text-xs text-amber-600">Sin email</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap" data-testid="personal-password-cell">
                        {u.plain_password ? (
                          <span className="inline-flex items-center gap-1.5">
                            <code className="font-mono text-gray-900">{shownPwdIds.has(u.id) ? u.plain_password : '••••••••'}</code>
                            <button type="button" onClick={() => togglePwdVisibility(u.id)} data-testid="toggle-password-visibility" title={shownPwdIds.has(u.id) ? 'Ocultar' : 'Mostrar'} className="p-1 rounded hover:bg-gray-100 text-gray-500">
                              {shownPwdIds.has(u.id) ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                            </button>
                            {shownPwdIds.has(u.id) && (
                              <button type="button" onClick={() => copyToClipboard(u.plain_password)} data-testid="copy-row-password" title="Copiar" className="p-1 rounded hover:bg-gray-100 text-gray-500">
                                <Copy className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400" title="Restablece la contraseña con el botón de llave para generarla y verla aquí">No disponible</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{u.department_name}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-red-50 text-[#DC143C] font-medium">
                          {u.project_name}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{u.job_title || '-'}</td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => handleEditClick(u)} data-testid="edit-personal-button" title="Editar" className="text-gray-700 hover:bg-gray-100">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleResetPassword(u)} data-testid="reset-password-button" title="Restablecer contraseña" className="text-amber-600 hover:bg-amber-50">
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" data-testid="delete-personal-button" onClick={() => handleDelete(u)} title="Eliminar" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {pageInfo.total > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50" data-testid="personal-pagination">
              <p className="text-sm text-gray-600">
                Página <strong>{page}</strong> de <strong>{pageInfo.pages}</strong> · {pageInfo.total} registro{pageInfo.total !== 1 ? 's' : ''} (recientes primero)
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="pagination-prev" className="flex items-center gap-1">
                  <ChevronLeft className="h-4 w-4" /> Anterior
                </Button>
                {Array.from({ length: pageInfo.pages }, (_, i) => i + 1)
                  .filter((n) => n === 1 || n === pageInfo.pages || Math.abs(n - page) <= 2)
                  .map((n, i, arr) => (
                    <React.Fragment key={n}>
                      {i > 0 && arr[i - 1] !== n - 1 && <span className="px-1 text-gray-400">…</span>}
                      <Button
                        variant={n === page ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPage(n)}
                        data-testid={`pagination-page-${n}`}
                        className={n === page ? 'bg-[#DC143C] hover:bg-[#B91C1C] text-white' : ''}
                      >
                        {n}
                      </Button>
                    </React.Fragment>
                  ))}
                <Button variant="outline" size="sm" disabled={page >= pageInfo.pages} onClick={() => setPage(page + 1)} data-testid="pagination-next" className="flex items-center gap-1">
                  Siguiente <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Dialog Crear / Editar */}
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); } }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto" aria-describedby={undefined} onOpenAutoFocus={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold uppercase tracking-tight">{editingId ? 'Editar Personal' : 'Nuevo Personal'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {!editingId && (
                <>
                  {/* Autocomplete: Buscar empleado existente para autocompletar datos */}
                  <div className="bg-blue-50/60 border border-blue-200 rounded-lg p-3">
                    <Label className="text-xs font-semibold text-blue-900 mb-1.5 block uppercase tracking-wide flex items-center gap-1.5">
                      <Search className="h-3.5 w-3.5" />
                      Buscar empleado existente (opcional)
                    </Label>
                    <p className="text-xs text-blue-800/80 mb-2">
                      Selecciona un empleado para autocompletar nombre, email y departamento y evitar duplicar datos.
                    </p>
                    <div className="relative">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        <Input
                          data-testid="employee-autocomplete-input"
                          value={empQuery}
                          onChange={(e) => { setEmpQuery(e.target.value); setEmpOpen(true); setSelectedEmployeeId(null); }}
                          onFocus={() => setEmpOpen(true)}
                          onBlur={() => setTimeout(() => setEmpOpen(false), 150)}
                          placeholder="Escribe nombre o email del empleado..."
                          className="h-11 pl-9 pr-9 bg-white"
                          autoComplete="off"
                        />
                        {empQuery && (
                          <button
                            type="button"
                            data-testid="employee-autocomplete-clear"
                            onClick={handleClearSelectedEmployee}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100"
                            title="Limpiar"
                          >
                            <X className="h-4 w-4 text-gray-500" />
                          </button>
                        )}
                      </div>
                      {empOpen && (
                        <div
                          data-testid="employee-autocomplete-dropdown"
                          className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-y-auto"
                        >
                          {filteredEmployees.length === 0 ? (
                            <div className="p-3 text-sm text-gray-500 text-center">No se encontraron empleados</div>
                          ) : (
                            filteredEmployees.map((emp) => {
                              const deptName = departments.find((d) => d.id === emp.department_id)?.name || '—';
                              const alreadyPersonal = usedEmails.has((emp.email || '').toLowerCase());
                              return (
                                <button
                                  type="button"
                                  key={emp.id}
                                  data-testid="employee-autocomplete-option"
                                  onMouseDown={(e) => { e.preventDefault(); handleSelectEmployee(emp); }}
                                  className={`w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 ${selectedEmployeeId === emp.id ? 'bg-blue-50' : ''}`}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium text-gray-900 truncate">{emp.name}</p>
                                      <p className="text-xs text-gray-500 truncate">{emp.email} · {deptName}</p>
                                    </div>
                                    {alreadyPersonal && (
                                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 flex-shrink-0">Ya es personal</span>
                                    )}
                                  </div>
                                </button>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                    {selectedEmployeeId && (
                      <div className="mt-2 text-xs text-green-700 flex items-center gap-1.5" data-testid="employee-selected-indicator">
                        <UserCheck className="h-3.5 w-3.5" />
                        Empleado vinculado · campos autocompletados (puedes editarlos antes de guardar)
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Usuario *</Label>
                    <div className="flex gap-2">
                      <Input data-testid="personal-username-input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="JPGarcia" className="h-11 flex-1" required />
                      <Button type="button" variant="outline" onClick={handleGenerateUsername} data-testid="generate-username-button" className="h-11 whitespace-nowrap flex items-center gap-1.5" title="Generar desde el nombre completo">
                        <Wand2 className="h-4 w-4" /> Generar
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Regla: 1ª letra del nombre + 1ª letra del apellido paterno + apellido materno (ej. Juan Pérez García → JPGarcia). Escribe primero el nombre completo.</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Contraseña *</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input data-testid="personal-password-input" type={showFormPwd ? 'text' : 'password'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11 pr-10" required />
                        <button type="button" onClick={() => setShowFormPwd(!showFormPwd)} data-testid="toggle-form-password" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-500 hover:text-gray-700">
                          {showFormPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <Button type="button" variant="outline" onClick={handleGeneratePassword} data-testid="generate-password-button" className="h-11 whitespace-nowrap flex items-center gap-1.5" title="Generar contraseña del sistema">
                        <Wand2 className="h-4 w-4" /> Generar
                      </Button>
                    </div>
                  </div>
                </>
              )}
              {editingId && (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm">
                  <p className="text-xs uppercase font-medium text-gray-500 mb-1">Usuario</p>
                  <p className="font-mono">{form.username}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Para cambiar la contraseña usa el botón <KeyRound className="inline h-3 w-3 mx-1" /> en la tabla.
                  </p>
                </div>
              )}
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Nombre completo *</Label>
                <Input data-testid="personal-fullname-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Juan Pérez" className="h-11" required />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Departamento *</Label>
                <Select value={form.department_id} onValueChange={(v) => setForm({ ...form, department_id: v })}>
                  <SelectTrigger data-testid="personal-dept-select" className="h-11"><SelectValue placeholder="Seleccione" /></SelectTrigger>
                  <SelectContent>
                    {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Proyecto *</Label>
                <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                  <SelectTrigger data-testid="personal-project-select" className="h-11"><SelectValue placeholder={projects.length === 0 ? 'Crea primero un proyecto' : 'Seleccione'} /></SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {projects.length === 0 && <p className="text-amber-600 text-xs mt-1">No hay proyectos. Ve al menú "Proyectos" para crear uno.</p>}
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Email</Label>
                <Input data-testid="personal-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="juan@empresa.com" className="h-11" />
                <p className="text-xs text-gray-500 mt-1">
                  {editingId
                    ? 'Para recibir notificaciones de cursos asignados y anuncios de Intranet.'
                    : 'Si lo proporcionas, recibirá un email con sus credenciales y el enlace de acceso.'}
                </p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Puesto</Label>
                <Input data-testid="personal-job-input" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} placeholder="Ingeniero de producción" className="h-11" />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" data-testid="save-personal-button" disabled={loading} className="bg-[#DC143C] hover:bg-[#B91C1C] text-white">{loading ? 'Guardando...' : 'Guardar'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Carga Masiva */}
        <Dialog open={bulkOpen} onOpenChange={(v) => { setBulkOpen(v); if (!v) { setBulkFile(null); setBulkResults(null); } }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="bulk-upload-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold uppercase tracking-tight flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-[#DC143C]" />
                Carga masiva de personal
              </DialogTitle>
            </DialogHeader>
            {!bulkResults ? (
              <div className="space-y-4 mt-2">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900 space-y-2">
                  <p className="font-semibold">¿Cómo funciona?</p>
                  <ol className="list-decimal ml-4 space-y-1 text-blue-800">
                    <li>Descarga la plantilla Excel y llena una fila por empleado. La hoja <strong>"Catálogos"</strong> muestra los departamentos y proyectos válidos.</li>
                    <li>El sistema genera automáticamente el <strong>usuario</strong> (1ª letra del nombre + 1ª letra del apellido paterno + apellido materno) y una <strong>contraseña</strong>.</li>
                    <li>Si un usuario ya está dado de alta, se marcará como <strong>duplicado</strong> para que lo revises.</li>
                    <li>A cada usuario creado se le asignan automáticamente <strong>todos los cursos existentes</strong>.</li>
                    <li>Si la fila incluye un email válido, recibirá UN correo con sus credenciales y la lista de cursos.</li>
                  </ol>
                </div>
                <Button variant="outline" onClick={handleDownloadTemplate} data-testid="download-template-button" className="w-full h-11 flex items-center justify-center gap-2">
                  <Download className="h-4 w-4" /> Descargar plantilla Excel
                </Button>
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Archivo Excel (.xlsx) *</Label>
                  <Input type="file" accept=".xlsx,.xlsm" onChange={(e) => setBulkFile(e.target.files?.[0] || null)} data-testid="bulk-file-input" className="h-11 pt-2" />
                </div>
                <div className="flex gap-2 justify-end pt-2">
                  <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancelar</Button>
                  <Button onClick={handleBulkUpload} disabled={!bulkFile || bulkLoading} data-testid="bulk-upload-submit" className="bg-[#DC143C] hover:bg-[#B91C1C] text-white">
                    {bulkLoading ? 'Procesando...' : 'Subir y procesar'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4 mt-2" data-testid="bulk-results">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-green-50 border border-green-200 rounded p-3">
                    <p className="text-2xl font-bold text-green-700" data-testid="bulk-created-count">{bulkResults.created}</p>
                    <p className="text-xs uppercase text-green-800">Creados</p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded p-3">
                    <p className="text-2xl font-bold text-amber-700" data-testid="bulk-duplicates-count">{bulkResults.duplicates}</p>
                    <p className="text-xs uppercase text-amber-800">Duplicados</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded p-3">
                    <p className="text-2xl font-bold text-red-700" data-testid="bulk-errors-count">{bulkResults.errors}</p>
                    <p className="text-xs uppercase text-red-800">Errores</p>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-x-auto max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Fila</th>
                        <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Nombre</th>
                        <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Usuario</th>
                        <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Contraseña</th>
                        <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Estatus</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {bulkResults.results.map((r) => (
                        <tr key={r.row} data-testid="bulk-result-row" className={r.status === 'creado' ? '' : r.status === 'duplicado' ? 'bg-amber-50' : 'bg-red-50'}>
                          <td className="px-3 py-2">{r.row}</td>
                          <td className="px-3 py-2">{r.full_name || '—'}</td>
                          <td className="px-3 py-2 font-mono">{r.username || '—'}</td>
                          <td className="px-3 py-2 font-mono">{r.password || '—'}</td>
                          <td className="px-3 py-2 text-xs">
                            {r.status === 'creado'
                              ? <span className="text-green-700 font-medium">✓ Creado</span>
                              : <span className={r.status === 'duplicado' ? 'text-amber-700' : 'text-red-700'}>{r.error}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  ⚠️ Descarga las credenciales antes de cerrar si necesitas compartirlas manualmente (también quedan visibles en la tabla de Personal).
                </p>
                <div className="flex gap-2 justify-between">
                  <Button variant="outline" onClick={exportBulkResults} data-testid="export-bulk-results" className="flex items-center gap-1.5">
                    <Download className="h-4 w-4" /> Descargar credenciales (CSV)
                  </Button>
                  <Button onClick={() => setBulkOpen(false)} data-testid="close-bulk-dialog">Cerrar</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* AlertDialog: Usuario duplicado (mensaje emergente) */}
        <AlertDialog open={!!dupAlert} onOpenChange={(v) => !v && setDupAlert(null)}>
          <AlertDialogContent data-testid="duplicate-user-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Usuario ya registrado
              </AlertDialogTitle>
              <AlertDialogDescription>{dupAlert}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={() => setDupAlert(null)} data-testid="duplicate-user-ok" className="bg-amber-600 hover:bg-amber-700">
                Entendido, lo reviso
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Modal de resultado de Reset Password */}
        {resetResult && (
          <Dialog open={true} onOpenChange={() => setResetResult(null)}>
            <DialogContent data-testid="reset-result-dialog" aria-describedby={undefined}>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold uppercase tracking-tight flex items-center gap-2">
                  <KeyRound className="h-5 w-5 text-amber-600" />
                  Contraseña restablecida
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-3 text-sm">
                <p>Se generó una nueva contraseña para <strong>{resetResult.fullName}</strong>.</p>
                {resetResult.emailSent ? (
                  <div className="bg-green-50 border border-green-200 rounded p-3 text-green-800 flex items-start gap-2">
                    <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Se envió por correo a <strong>{resetResult.email}</strong>. Por seguridad, ya no se muestra en pantalla.</span>
                  </div>
                ) : (
                  <>
                    <div className="bg-amber-50 border border-amber-200 rounded p-3 text-amber-800 text-xs">
                      ⚠️ {!resetResult.emailConfigured
                        ? <span>SMTP no está configurado en el backend. <strong>Copia la contraseña</strong> y compártela de forma segura.</span>
                        : !resetResult.userHasEmail
                        ? <span>El usuario <strong>no tiene email registrado</strong>. Copia la contraseña y compártela.</span>
                        : <span>El envío del correo falló (revisa los logs del backend). <strong>Copia la contraseña</strong> y compártela.</span>
                      }
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded p-3">
                      <p className="text-xs uppercase font-medium text-gray-500 mb-1">Nueva contraseña</p>
                      <div className="flex items-center justify-between gap-2">
                        <code className="font-mono text-base text-gray-900 break-all">{resetResult.newPassword}</code>
                        <Button size="sm" variant="outline" onClick={() => copyToClipboard(resetResult.newPassword)} data-testid="copy-password-button">
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <DialogFooter>
                <Button onClick={() => setResetResult(null)} data-testid="close-reset-dialog">Cerrar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* AlertDialog: Confirmar Reset Password */}
        <AlertDialog open={!!resetConfirm} onOpenChange={(v) => !v && setResetConfirm(null)}>
          <AlertDialogContent data-testid="reset-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-amber-600" />
                Restablecer contraseña
              </AlertDialogTitle>
              <AlertDialogDescription>
                {resetConfirm && (
                  <>
                    Se generará una nueva contraseña aleatoria para <strong>{resetConfirm.full_name}</strong>.
                    {resetConfirm.email && emailStatus?.enabled
                      ? <span> Se enviará por correo a <strong>{resetConfirm.email}</strong>.</span>
                      : !resetConfirm.email
                      ? <span> El usuario no tiene email registrado, la contraseña se mostrará en pantalla.</span>
                      : <span> SMTP no configurado: la contraseña se mostrará en pantalla.</span>}
                    {' '}La contraseña actual quedará invalidada.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="reset-confirm-cancel">Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={confirmResetPassword} data-testid="reset-confirm-ok" className="bg-amber-600 hover:bg-amber-700">
                Sí, restablecer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* AlertDialog: Confirmar Eliminar */}
        <AlertDialog open={!!deleteConfirm} onOpenChange={(v) => !v && setDeleteConfirm(null)}>
          <AlertDialogContent data-testid="delete-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-600" />
                Eliminar personal
              </AlertDialogTitle>
              <AlertDialogDescription>
                {deleteConfirm && (
                  <>¿Eliminar a <strong>{deleteConfirm.full_name}</strong> ({deleteConfirm.username})? Esta acción no se puede deshacer.</>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-confirm-cancel">Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} data-testid="delete-confirm-ok" className="bg-red-600 hover:bg-red-700">
                Sí, eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
